import { useCallback, useEffect, useState } from "react";
import _ from "lodash";

import { HourglassEmptyIcon } from "icons";
import { colors } from "theme/palette";
import { format, FORMATS, parseJSON } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, { DialogContent, DialogTitle } from "components/Dialog";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";
import toast from "components/Toast";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import {
  IChecklistInvitation,
  IDealInvitation,
  useDealAndChecklistPendingInvitations,
} from "entities/Deal/sdk";
import { shareTokenAccept, shareTokenReject } from "entities/Package/sdk";
import { invitationAccept, invitationReject } from "entities/Team/sdk";

export interface IDealAndChecklistPendingInvitationsDialog {
  invitationAcceptCallback: () => void;
}

const DealAndChecklistPendingInvitationsDialog = ({
  invitationAcceptCallback,
}: IDealAndChecklistPendingInvitationsDialog) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const { data, mutate: refetchInvitations } =
    useDealAndChecklistPendingInvitations();

  useEffect(() => {
    setIsOpen(!_.isEmpty(data));
  }, [data]);

  const handleDealInvitationReject = useCallback(
    ({ invitation }: { invitation: IDealInvitation }) => {
      showConfirmationDialog({
        onConfirm: () => {
          setIsSubmitting(true);

          invitationReject({ invitationId: invitation.id })
            .then(() => {
              toast.successMessage("Invitation has been rejected successfully");
              refetchInvitations();
            })
            .catch(handleInvalidRequest)
            .finally(() => setIsSubmitting(false));
        },
        message:
          "Are you sure you want to reject this invitation? This action cannot be reverted.",
        confirmButtonText: "Reject invitation",
      });
    },
    [showConfirmationDialog, refetchInvitations]
  );

  const handleDealInvitationAccept = useCallback(
    ({ invitation }: { invitation: IDealInvitation }) => {
      setIsSubmitting(true);
      invitationAccept({ invitationId: invitation.id })
        .then(() => {
          toast.successMessage("Invitation has been accepted successfully");
          refetchInvitations();
          invitationAcceptCallback();
        })
        .catch(handleInvalidRequest)
        .finally(() => setIsSubmitting(false));
    },
    [refetchInvitations, invitationAcceptCallback]
  );

  const handleChecklistInvitationReject = useCallback(
    ({ invitation }: { invitation: IChecklistInvitation }) => {
      showConfirmationDialog({
        onConfirm: () => {
          setIsSubmitting(true);
          shareTokenReject({ token: invitation.token })
            .then(() => {
              toast.successMessage("Invitation has been rejected successfully");
              refetchInvitations();
            })
            .catch(handleInvalidRequest)
            .finally(() => setIsSubmitting(false));
        },
        message:
          "Are you sure you want to reject this invitation? This action cannot be reverted.",
        confirmButtonText: "Reject invitation",
      });
    },
    [showConfirmationDialog, refetchInvitations]
  );

  const handleChecklistInvitationAccept = useCallback(
    ({ invitation }: { invitation: IChecklistInvitation }) => {
      setIsSubmitting(true);

      shareTokenAccept({ token: invitation.token })
        .then(() => {
          toast.successMessage("Invitation has been accepted successfully");
          refetchInvitations();
          invitationAcceptCallback();
        })
        .catch(handleInvalidRequest)
        .finally(() => setIsSubmitting(false));
    },
    [refetchInvitations, invitationAcceptCallback]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={isOpen}
      onClose={() => setIsOpen(false)}
      dialogDataTestid="invitations-dialog"
    >
      <DialogTitle
        sx={{
          margin: 2,
          borderRadius: "8px",
          backgroundColor: colors.blue10,
        }}
      >
        <Stack direction="row">
          <HourglassEmptyIcon height={26} sx={{ marginTop: 0.25 }} />

          <Stack>
            <Text variant="h2" marginBottom={0.5} color={colors.gray100}>
              Invites to collaborate
            </Text>
            <Text variant="text4" color={colors.gray60}>
              {data?.length} pending invites
            </Text>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent data-testid="dialog-content">
        {data?.map((invitation) =>
          invitation.invitation_type === "deal" ? (
            <Stack
              key={invitation.id}
              gap={3}
              padding={2.5}
              marginBottom={1.5}
              sx={{ border: `1px solid ${colors.blue60}`, borderRadius: "8px" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={2}
                data-testid="deal-invitation"
              >
                <Stack alignItems="flex-start" gap={0.25}>
                  <Tag label="DEAL TEAM INVITE" variant="blue20" />
                  <Text variant="text4" color={colors.gray60}>
                    Invite sent by {invitation.created_by.first_name}{" "}
                    {invitation.created_by.last_name} (
                    {invitation.created_by.email}) on{" "}
                    {format(
                      parseJSON(invitation.created_at),
                      FORMATS.FRONTEND.DATE
                    )}
                  </Text>
                </Stack>

                <Stack direction="row" gap={1}>
                  <Button
                    variant="outlined"
                    disabled={isSubmitting}
                    onClick={() => handleDealInvitationReject({ invitation })}
                    data-testid="deal-decline-button"
                  >
                    Decline
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={() => handleDealInvitationAccept({ invitation })}
                    data-testid="deal-accept-button"
                  >
                    Accept
                  </Button>
                </Stack>
              </Stack>

              <Stack gap={1} direction="row">
                <Stack flexBasis="33%">
                  <Text variant="text4" color={colors.gray60}>
                    Deal name
                  </Text>
                  <Text variant="text2" fontWeight={700}>
                    {invitation.deal.name}
                  </Text>
                </Stack>

                <Stack flexBasis="33%">
                  <Text variant="text4" color={colors.gray60}>
                    Owner
                  </Text>
                  <Text variant="text2">{invitation.deal.company.name}</Text>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack
              key={invitation.token}
              gap={3}
              padding={2.5}
              marginBottom={1.5}
              sx={{ border: `1px solid ${colors.blue60}`, borderRadius: "8px" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={2}
                data-testid="checklist-invitation"
              >
                <Stack alignItems="flex-start" gap={0.25}>
                  <Tag label="Checklist invite" variant="blue20" />
                  <Text variant="text4" color={colors.gray60}>
                    Invite sent by {invitation.created_by.first_name}{" "}
                    {invitation.created_by.last_name} (
                    {invitation.created_by.email}) on{" "}
                    {format(
                      parseJSON(invitation.created_at),
                      FORMATS.FRONTEND.DATE
                    )}
                  </Text>
                </Stack>

                <Stack direction="row" gap={1}>
                  <Button
                    variant="outlined"
                    disabled={isSubmitting}
                    onClick={() =>
                      handleChecklistInvitationReject({ invitation })
                    }
                    data-testid="checklist-decline-button"
                  >
                    Decline
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={() =>
                      handleChecklistInvitationAccept({ invitation })
                    }
                    data-testid="checklist-accept-button"
                  >
                    Accept
                  </Button>
                </Stack>
              </Stack>

              <Stack gap={1} direction="row">
                <Stack flexBasis="33%">
                  <Text variant="text4" color={colors.gray60}>
                    Checklist name
                  </Text>
                  <Text variant="text2" fontWeight={700}>
                    {invitation.company_package.name}
                  </Text>
                </Stack>

                <Stack flexBasis="33%">
                  <Text variant="text4" color={colors.gray60}>
                    Owner
                  </Text>
                  <Text variant="text2">{invitation.deal.company.name}</Text>
                </Stack>

                <Stack flexBasis="33%">
                  <Text variant="text4" color={colors.gray60}>
                    Deal context
                  </Text>
                  <Text variant="text2">{invitation.deal.name}</Text>
                </Stack>
              </Stack>
            </Stack>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DealAndChecklistPendingInvitationsDialog;
